import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Link } from "@tanstack/react-router";
import { AlertTriangle, Loader2, Sparkles } from "lucide-react";
import { z } from "zod";
import { askInteractionAi, type InteractionAiResult } from "@/lib/ai-interaction.functions";
import { useT } from "@/i18n/LanguageContext";
import type { StringKey } from "@/i18n/strings";

const formSchema = z.object({
  question: z
    .string()
    .trim()
    .min(10, "Bitte formuliere eine Frage mit mindestens 10 Zeichen.")
    .max(500, "Bitte kürze die Frage auf maximal 500 Zeichen."),
});

type FormValues = z.infer<typeof formSchema>;

type Props = {
  aName: string;
  bName: string;
  aId: string | null;
  bId: string | null;
};

export function InteractionAiAnswer({ aName, bName, aId, bId }: Props) {
  const t = useT();
  const ask = useServerFn(askInteractionAi);
  const pairKey = `${aId ?? ""}|${bId ?? ""}`;
  const ready = Boolean(aId && bId && aId !== bId);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { question: "" },
  });

  const mutation = useMutation<InteractionAiResult, Error, FormValues>({
    mutationFn: async (values) => {
      if (!aId || !bId) throw new Error("no-pair");
      return ask({
        data: { substanceAId: aId, substanceBId: bId, question: values.question, lang: "de" },
      });
    },
  });

  // A new pair invalidates the previous answer.
  useEffect(() => {
    mutation.reset();
    form.reset({ question: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pairKey]);

  const result = mutation.data;
  const errorText = useMemo(() => {
    if (mutation.isError) return t("ai.error.unavailable");
    if (result?.status === "limit") return t("ai.error.limit");
    if (result?.status === "error") return t(`ai.error.${result.reason}` as StringKey);
    return null;
  }, [mutation.isError, result, t]);

  return (
    <section
      className="glass-card"
      style={{ padding: "1.25rem", display: "flex", flexDirection: "column", gap: "0.85rem", marginTop: "1rem" }}
      aria-labelledby="ai-answer-heading"
    >
      <h2
        id="ai-answer-heading"
        style={{ margin: 0, display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "1rem", color: "var(--accent-strong, var(--accent))" }}
      >
        <Sparkles size={16} aria-hidden />
        {t("ai.title")}
      </h2>
      <p style={{ margin: 0, fontSize: "0.82rem", lineHeight: 1.5, color: "var(--text-secondary, var(--text-primary))" }}>
        {t("ai.intro")}
      </p>

      {!ready ? (
        <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--text-muted)" }}>{t("ai.pairHint")}</p>
      ) : (
        <form
          onSubmit={form.handleSubmit((values) => mutation.mutate(values))}
          style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}
        >
          <label htmlFor="ai-question" style={{ fontSize: "0.82rem", color: "var(--text-secondary, var(--text-primary))" }}>
            {t("ai.questionLabel")} ({aName} + {bName})
          </label>
          <textarea
            id="ai-question"
            rows={3}
            maxLength={500}
            placeholder={t("ai.placeholder")}
            {...form.register("question")}
            style={{
              background: "var(--bg-surface)",
              color: "var(--text-primary)",
              border: "1px solid var(--accent-border)",
              borderRadius: 8,
              padding: "0.6rem 0.75rem",
              fontSize: "0.9rem",
              resize: "vertical",
            }}
          />
          {form.formState.errors.question && (
            <span role="alert" style={{ fontSize: "0.8rem", color: "var(--text-secondary, var(--text-primary))" }}>
              {form.formState.errors.question.message}
            </span>
          )}
          <button
            type="submit"
            disabled={mutation.isPending}
            style={{
              alignSelf: "flex-start",
              display: "inline-flex",
              alignItems: "center",
              gap: "0.45rem",
              minHeight: 44,
              padding: "0.5rem 1.1rem",
              borderRadius: 8,
              border: "1px solid var(--accent-border)",
              background: "var(--accent-bg)",
              color: "var(--text-primary)",
              fontSize: "0.88rem",
              cursor: mutation.isPending ? "progress" : "pointer",
            }}
          >
            {mutation.isPending && <Loader2 size={15} className="animate-spin" aria-hidden />}
            {t("ai.submit")}
          </button>
        </form>
      )}

      <div aria-live="polite" style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
        {mutation.isPending && (
          <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--text-muted)" }}>{t("ai.loading")}</p>
        )}

        {result?.status === "noAssessment" && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.5rem",
              padding: "0.85rem 1rem",
              borderRadius: 8,
              border: "1px solid var(--accent-border)",
              background: "rgba(255,255,255,0.02)",
              fontSize: "0.85rem",
              lineHeight: 1.55,
            }}
          >
            <span>{t("ai.noAssessment")}</span>
            <Link to="/notfall" style={{ color: "var(--accent-strong, var(--accent))", fontSize: "0.85rem" }}>
              {t("ai.emergencyLink")}
            </Link>
          </div>
        )}

        {errorText && (
          <div
            role="alert"
            style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start", fontSize: "0.85rem", lineHeight: 1.5 }}
          >
            <AlertTriangle size={15} aria-hidden style={{ flexShrink: 0, marginTop: 2 }} />
            <span>{errorText}</span>
          </div>
        )}

        {result?.status === "ok" && (
          <article style={{ display: "flex", flexDirection: "column", gap: "0.65rem" }}>
            <h3 style={{ margin: 0, fontSize: "0.9rem", color: "var(--text-secondary, var(--text-primary))" }}>
              {t("ai.answerTitle")}: {result.pair.a} + {result.pair.b}
            </h3>
            <div style={{ fontSize: "0.9rem", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{result.answer}</div>
            {result.sources.length > 0 && (
              <div>
                <h4 style={{ margin: "0 0 0.3rem", fontSize: "0.82rem", color: "var(--text-secondary, var(--text-primary))" }}>
                  {t("ai.sourcesTitle")}
                </h4>
                <ul style={{ margin: 0, paddingLeft: "1.1rem", fontSize: "0.82rem", lineHeight: 1.55 }}>
                  {result.sources.map((s) => (
                    <li key={s.key}>
                      <span className="mono-badge">{s.key}</span>{" "}
                      {s.url ? (
                        <a href={s.url} target="_blank" rel="noreferrer noopener" style={{ color: "var(--accent-strong, var(--accent))" }}>
                          {s.title}
                        </a>
                      ) : (
                        s.title
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <p style={{ margin: 0, fontSize: "0.78rem", color: "var(--text-muted)", lineHeight: 1.5 }}>
              {t("ai.disclaimer")}
            </p>
          </article>
        )}
      </div>
    </section>
  );
}
